import type { z } from "zod";
import * as Sentry from "@sentry/react";
import { tg } from "@/lib/telegram";
import { getPlatform } from "@/lib/platform";
import { deviceAuthenticate } from "./deviceAuth";
import { adminWebAuthenticate } from "./adminWebAuth";
import { getAuthMode } from "./authMode";
import {
  SessionEnvelopeSchema,
  useSessionStore,
  type SessionEnvelope,
} from "@/store/session";

export const API_BASE_URL: string = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API error ${status}`);
    this.name = "ApiError";
  }
}

export class UnauthorizedError extends Error {
  constructor() {
    super("Unauthorized");
    this.name = "UnauthorizedError";
  }
}

export class SchemaError extends Error {
  constructor(public readonly issues: z.ZodIssue[]) {
    super("Response failed schema validation");
    this.name = "SchemaError";
  }
}

export interface RequestConfig<T> {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  body?: unknown;
  // `z.ZodType<T>` would pin zod's *input* type to `T` as well, so a schema
  // carrying a `.default()` or a `.transform()` resolves `T` to its input
  // shape — the one where the defaulted fields are still optional — even
  // though `parse` hands back the output. The response body is `unknown`
  // before it is parsed, which is exactly what this says.
  schema: z.ZodType<T, z.ZodTypeDef, unknown>;
  signal?: AbortSignal;
  skipAuthRetry?: boolean;
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export async function rawFetch(
  path: string,
  init: RequestInit,
): Promise<{ status: number; ok: boolean; body: unknown }> {
  const response = await fetch(buildUrl(path), init);
  const text = await response.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  return { status: response.status, ok: response.ok, body };
}

let refreshInflight: Promise<string> | null = null;

/** DUE-141: read the device's IANA tz from Intl. Telegram does not
 * expose this in initData, but the mini app is a Chromium WebView so
 * `Intl.DateTimeFormat` is reliably present. Wrapped in try/catch
 * because an exotic environment that throws (vitest jsdom without an
 * Intl shim, certain hardened browsers) must not block auth — the
 * backend treats absence as "keep whatever notify_tz is on file." */
export function detectClientTz(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || undefined;
  } catch {
    return undefined;
  }
}

async function reauthenticate(): Promise<string> {
  if (getAuthMode() === "admin-web") {
    // DUE-221: browser admin has neither initData nor a device identity —
    // refresh-token-only. A dead family throws UnauthorizedError and the
    // admin shell routes to /login.
    const envelope = await adminWebAuthenticate();
    useSessionStore.getState().setSession(envelope);
    return envelope.jwt;
  }
  if (getPlatform().name === "ios" || getPlatform().name === "web") {
    // DUE-158 / DUE-318: iOS and the browser have no Telegram initData to
    // reauth with — refresh-token -> device-guest fallback lives in
    // deviceAuth.ts (Task 5 / Task 10).
    const envelope = await deviceAuthenticate();
    useSessionStore.getState().setSession(envelope);
    return envelope.jwt;
  }
  const initData = tg.initData;
  if (!initData) {
    throw new UnauthorizedError();
  }
  const tz = detectClientTz();
  const requestBody: { init_data: string; client_tz?: string } = { init_data: initData };
  if (tz) requestBody.client_tz = tz;
  const { body, ok, status } = await rawFetch("/api/auth/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });
  if (!ok) {
    throw new ApiError(status, body);
  }
  const parsed = SessionEnvelopeSchema.safeParse(body);
  if (!parsed.success) {
    throw new SchemaError(parsed.error.issues);
  }
  const envelope: SessionEnvelope = parsed.data;
  useSessionStore.getState().setSession(envelope);
  return envelope.jwt;
}

async function ensureFreshJwt(): Promise<string> {
  if (!refreshInflight) {
    refreshInflight = reauthenticate().finally(() => {
      refreshInflight = null;
    });
  }
  return refreshInflight;
}

/** Boot-time auth: guarantee a session exists before the onboarding UI
 * renders. The wizard's welcome screen has a locale switcher, and the
 * switch needs `user` in the store to fire `PATCH /api/me`. Without this
 * call, the first API request only happens on ConfirmPage — far too
 * late, the natal reading is already queued with the stale locale.
 *
 * Safe to call repeatedly: `ensureFreshJwt` dedupes in-flight requests
 * and `rawFetch` uses `tg.initData` which is stable across the session.
 */
export async function ensureSession(): Promise<void> {
  const state = useSessionStore.getState();
  if (state.jwt && state.user) return;
  try {
    await ensureFreshJwt();
  } catch (err) {
    // DUE-149: a SchemaError here means the server answered 200 but with a
    // body the FE schema rejects (a backend/frontend contract desync).
    // Swallowing it whole made the failure invisible — clean server logs,
    // no session, user stuck on /onboarding/welcome. Report it so the
    // desync is observable even though we still don't block the UI.
    if (err instanceof SchemaError) {
      Sentry.captureException(err, { tags: { auth_step: "ensure_session" } });
      return;
    }
    // Non-Telegram contexts (vitest, desktop web preview) have no
    // `initData`. Swallow — the onboarding UI still works locally; the
    // locale switch just won't persist to the backend until the user
    // is inside a real Telegram shell.
    // DUE-158 / DUE-318: on iOS and the web, `deviceAuthenticate` always
    // yields a session (device-guest at worst), so this swallow only ever
    // covers vitest or a Telegram build running outside Telegram.
  }
}

export async function request<T>({
  method = "GET",
  path,
  body,
  schema,
  signal,
  skipAuthRetry = false,
}: RequestConfig<T>): Promise<T> {
  const buildInit = (jwt: string | null): RequestInit => {
    const headers: Record<string, string> = { Accept: "application/json" };
    // DUE-180: lets the backend evaluate platform-scoped feature flags
    // (e.g. an ios-only rollout) on every request, not just /api/me.
    headers["X-Duetto-Platform"] = getPlatform().name;
    if (jwt) headers.Authorization = `Bearer ${jwt}`;
    const init: RequestInit = { method, headers, signal };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }
    return init;
  };

  // If a refresh is already in flight (triggered by an earlier parallel
  // request or bootstrap), wait for it before sending the first attempt.
  // Without this, each parallel caller reads the still-stale JWT from the
  // store, burns one 401, then queues behind the same inflight refresh —
  // producing N 401s on the wire for N parallel requests.
  if (!skipAuthRetry && refreshInflight) {
    try {
      await refreshInflight;
    } catch {
      // Fall through: the 401 path below will surface the error.
    }
  }

  const initialJwt = useSessionStore.getState().jwt;
  let response = await rawFetch(path, buildInit(initialJwt));

  if (response.status === 401 && !skipAuthRetry) {
    try {
      const freshJwt = await ensureFreshJwt();
      response = await rawFetch(path, buildInit(freshJwt));
    } catch {
      useSessionStore.getState().clear();
      throw new UnauthorizedError();
    }
  }

  if (response.status === 401) {
    useSessionStore.getState().clear();
    throw new UnauthorizedError();
  }

  if (!response.ok) {
    throw new ApiError(response.status, response.body);
  }

  const parsed = schema.safeParse(response.body);
  if (!parsed.success) {
    throw new SchemaError(parsed.error.issues);
  }
  return parsed.data;
}

/** Testing hook: reset the in-flight refresh promise between tests. */
export function __resetAuthRefreshForTests(): void {
  refreshInflight = null;
}
